import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {MvsCrudService} from 'features/core/shared/service/crud/mvs-crud.service';
import {mvsCrudRequestHttpOptions} from 'features/core/shared/service/crud/mvs-crud-request-options';
import {DtoDetail} from 'features/core/shared/dto/dto.detail';
import {MvsObjectActionEnum} from 'features/core/shared/service/object/mvs-object-action.enum';
import {ObjectChangeInformationActionEnum} from 'features/core/shared/logic/object-change-information';
import {PdPartnerJobStartResultDto} from '../../model/dto/api/pd-partner-job-start-result.dto';
import {PdPartnerReleaseLockResultDto} from '../../model/dto/api/pd-partner-release-lock-result.dto';
import {PdPartnerProvisionJobsResultDto} from '../../model/dto/api/pd-partner-provision-jobs-result.dto';
import {PdCollectionModeEnum} from '../../model/dto/enum/pd-collection-mode.enum';

/**
 * CRUD service of {@code pd.PdPartner} (Datenaustausch-Partner) plus the four action endpoints of
 * {@code PdPartnerController}.
 *
 * Generic CRUD (list, get, create, update, delete) is inherited from {@link MvsCrudService}. A PUT that lost the
 * optimistic-lock race against a pd job answers HTTP 409 with a German text.
 *
 * The three read-only entities of the module ({@code pd.PdPartnerCollectionRun}, {@code pd.PdPartnerExportRun},
 * {@code pd.PdPartnerContractEvent}) have NO service file on purpose: they need nothing beyond the generic CRUD
 * read and are registered route-only in the entity-provider (fe_core_mvs-crud-service SC6/SC7).
 *
 * ACTION ERRORS: the four action POSTs opt out of the global error toast
 * ({@code mvsCrudRequestHttpOptions(true)}); the calling component shows the server text itself.
 * Every refusal (lock held, inactive partner, missing or duplicate job row, missing write right —
 * a {@code @PreAuthorize} denial also arrives as 400) is an HTTP 400 whose body carries a German text.
 * The Observables here do not swallow it.
 *
 * Each action notifies the object service afterwards, because the partner row (processing lock, last runs)
 * changes under the user's open page.
 */
@Injectable({
    providedIn: 'root'
})
export class PdPartnerService extends MvsCrudService {

    constructor(protected override http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/pd/pdPartners');
    }

    /**
     * Starts a collection run manually. The run starts ASYNCHRONOUSLY after the synchronous checks, so the answer
     * names the job type and the override parameters but no job-run id — reload the run lists afterwards.
     *
     * {@code POST /mvsa/pd/pdPartners/{id}/startCollection?mode=DELTA|FULL&dryRun=true|false}
     *
     * @param id     the partner
     * @param mode   DELTA (regular) or FULL (Vollabgleich)
     * @param dryRun true = Probelauf, the decision is computed and counted but nothing is written to the ledger
     */
    startCollection(id: number, mode: PdCollectionModeEnum, dryRun: boolean): Observable<PdPartnerJobStartResultDto> {
        const params = new HttpParams()
            .set('mode', PdCollectionModeEnum[mode])
            .set('dryRun', dryRun);

        return this.http.post<PdPartnerJobStartResultDto>(
            `${this.apiUrl}/${id}/startCollection`, null, {params, ...mvsCrudRequestHttpOptions(true)}).pipe(
            tap(() => this.notifyUpdated(id))
        );
    }

    /**
     * Starts an export run manually (asynchronous, same answer shape as {@link #startCollection}).
     *
     * {@code POST /mvsa/pd/pdPartners/{id}/startExport}
     */
    startExport(id: number): Observable<PdPartnerJobStartResultDto> {
        return this.http.post<PdPartnerJobStartResultDto>(
            `${this.apiUrl}/${id}/startExport`, null, mvsCrudRequestHttpOptions(true)).pipe(
            tap(() => this.notifyUpdated(id))
        );
    }

    /**
     * Releases the processing lock of a partner. Refused with HTTP 400 while jb still reports an active
     * execution for the run that holds the lock (DC-3) — release is for stuck locks only. HTTP 200 with
     * {@code released: false} means the partner was not locked.
     *
     * {@code POST /mvsa/pd/pdPartners/{id}/releaseLock}
     */
    releaseLock(id: number): Observable<PdPartnerReleaseLockResultDto> {
        return this.http.post<PdPartnerReleaseLockResultDto>(
            `${this.apiUrl}/${id}/releaseLock`, null, mvsCrudRequestHttpOptions(true)).pipe(
            tap(() => this.notifyUpdated(id))
        );
    }

    /**
     * Sets up the two job rows (collection, export) of the partner as drafts. Idempotent. A draft row is not
     * scheduled — someone still has to activate it on the jb configuration page.
     *
     * {@code POST /mvsa/pd/pdPartners/{id}/provisionJobs}
     */
    provisionJobs(id: number): Observable<PdPartnerProvisionJobsResultDto> {
        return this.http.post<PdPartnerProvisionJobsResultDto>(
            `${this.apiUrl}/${id}/provisionJobs`, null, mvsCrudRequestHttpOptions(true)).pipe(
            tap(() => this.notifyUpdated(id))
        );
    }

    /**
     * Same notification pair as {@link MvsCrudService#restore}: the processed-action event plus the broadcast
     * change event, so every open list or page refreshes.
     */
    private notifyUpdated(id: number): void {
        this.informObjectService(id, MvsObjectActionEnum.Updated);

        const dtoDetail = new DtoDetail();
        dtoDetail.id = id;
        this.subscribeBroadcastChange(dtoDetail, ObjectChangeInformationActionEnum.updated);
    }

}
